/**
 * Expediente da operação comercial.
 *
 * Segunda a sexta, das 9h às 18h, no horário de São Paulo. Feriado conta como
 * dia de Expediente — decisão registrada no ADR 0008, para não pagar o custo de
 * um calendário de feriados por um ruído de cerca de uma dúzia de dias por ano.
 *
 * O fuso é declarado aqui, e não herdado do processo, de propósito: o contêiner
 * roda em UTC, e um Lead que entra às 23h em São Paulo é madrugada do dia
 * seguinte em UTC. Herdar o fuso daria a esse Lead um dia inteiro de prazo a
 * mais, sem erro visível em lugar nenhum.
 */
export const FUSO_DA_OPERACAO = 'America/Sao_Paulo';
/** Hora de abertura e de fechamento, no fuso da operação. */
export const EXPEDIENTE_ABRE_HORA = 9;
export const EXPEDIENTE_FECHA_HORA = 18;
const ABRE_EM_MINUTOS = EXPEDIENTE_ABRE_HORA * 60;
const FECHA_EM_MINUTOS = EXPEDIENTE_FECHA_HORA * 60;
const MINUTOS_POR_DIA_DE_EXPEDIENTE = FECHA_EM_MINUTOS - ABRE_EM_MINUTOS;
const MS_POR_DIA = 86_400_000;
/**
 * 1º de janeiro de 1970 caiu numa quinta-feira.
 *
 * O deslocamento converte índice de dia em dia da semana sem construir Date.
 */
const DIA_DA_SEMANA_NA_EPOCA = 4;
const FORMATADOR = new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSO_DA_OPERACAO,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
});
/**
 * Traduz um instante para o calendário e o relógio de São Paulo.
 *
 * É a única operação cara daqui — cada chamada passa pelo Intl — e por isso
 * acontece exatamente duas vezes por avaliação, nunca dentro de laço. A tela de
 * Leads avalia o SLA de até quinhentas entradas de uma vez; percorrer dia a dia
 * custava quase um segundo de CPU nesse mesmo caso.
 */
function momentoLocal(instante) {
    const partes = FORMATADOR.formatToParts(instante);
    const valor = (tipo) => Number(partes.find((parte) => parte.type === tipo)?.value ?? '0');
    const dia = Date.UTC(valor('year'), valor('month') - 1, valor('day')) / MS_POR_DIA;
    return {
        dia,
        minutoDoDia: valor('hour') * 60 + valor('minute') + valor('second') / 60,
    };
}
function ehDiaDeExpediente(dia) {
    const diaDaSemana = (((dia + DIA_DA_SEMANA_NA_EPOCA) % 7) + 7) % 7;
    return diaDaSemana !== 0 && diaDaSemana !== 6;
}
/** Quantos dias de Expediente existem no intervalo fechado entre dois dias. */
function diasDeExpedienteEntre(primeiro, ultimo) {
    if (ultimo < primeiro)
        return 0;
    const total = ultimo - primeiro + 1;
    const semanasCheias = Math.floor(total / 7);
    let dias = semanasCheias * 5;
    // No máximo seis dias sobram depois das semanas cheias, então a conta fecha
    // em tempo constante mesmo para um Lead esquecido há anos.
    for (let sobra = 0; sobra < total % 7; sobra += 1) {
        if (ehDiaDeExpediente(primeiro + semanasCheias * 7 + sobra))
            dias += 1;
    }
    return dias;
}
/**
 * Quanto de um dia cai dentro da janela, entre dois pontos do relógio local.
 *
 * Serve tanto ao dia inteiro quanto às pontas: o primeiro dia vai da entrada
 * até o fechamento, o último vai da abertura até agora, e um Lead que entrou e
 * foi atendido no mesmo dia usa os dois extremos de uma vez.
 */
function minutosNaJanela(deMinuto, ateMinuto) {
    return Math.max(0, Math.min(ateMinuto, FECHA_EM_MINUTOS) - Math.max(deMinuto, ABRE_EM_MINUTOS));
}
/**
 * Minutos de Expediente decorridos entre dois instantes.
 *
 * Noite, fim de semana e o intervalo entre o fechamento e a abertura seguinte
 * não contam. É o que faz o SLA de primeiro contato medir atendimento em vez de
 * relógio de parede.
 *
 * A conta corre no relógio de parede de São Paulo: um dia de Expediente vale
 * sempre 540 minutos, mesmo numa virada de horário de verão em que ele dure 8h
 * ou 10h de tempo real. É o significado que interessa ao indicador — quanto do
 * turno de trabalho passou, e não quantos segundos o relógio andou.
 */
export function minutosDeExpediente(de, ate) {
    if (ate.getTime() <= de.getTime())
        return 0;
    const inicio = momentoLocal(de);
    const fim = momentoLocal(ate);
    if (inicio.dia === fim.dia) {
        return ehDiaDeExpediente(inicio.dia)
            ? minutosNaJanela(inicio.minutoDoDia, fim.minutoDoDia)
            : 0;
    }
    let total = diasDeExpedienteEntre(inicio.dia + 1, fim.dia - 1) * MINUTOS_POR_DIA_DE_EXPEDIENTE;
    if (ehDiaDeExpediente(inicio.dia)) {
        total += minutosNaJanela(inicio.minutoDoDia, FECHA_EM_MINUTOS);
    }
    if (ehDiaDeExpediente(fim.dia)) {
        total += minutosNaJanela(ABRE_EM_MINUTOS, fim.minutoDoDia);
    }
    return total;
}
//# sourceMappingURL=expediente.js.map